import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  WritableSignal,
  computed,
  inject,
  signal,
} from '@angular/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { TicketDocument } from '../../core/models/ticket.model';
import { TicketService } from '../../core/services/ticket.service';
import {
  buildTicketAttachmentPreviewSource,
  canPreviewTicketAttachment,
} from '../../core/utils/ticket-attachment-preview.util';
import { FilePreviewSource } from '../shared/file-preview-modal/file-preview.types';
import { FilePreviewModalComponent } from '../shared/file-preview-modal/file-preview-modal.component';

const PLACEHOLDER_SVG =
  'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2270%22 height=%2270%22%3E%3Crect fill=%22%23f0f0f0%22 width=%2270%22 height=%2270%22/%3E%3Ctext x=%2250%25%22 y=%2250%25%22 text-anchor=%22middle%22 dy=%22.3em%22 font-size=%2214%22 fill=%22%23999%22%3E?%3C/text%3E%3C/svg%3E';

@Component({
  selector: 'app-ticket-attachments',
  standalone: true,
  imports: [ButtonModule, DialogModule, TooltipModule, FilePreviewModalComponent],
  templateUrl: './ticket-attachments.component.html',
  styleUrls: ['./ticket-attachments.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketAttachmentsComponent implements OnDestroy {
  private readonly ticketService = inject(TicketService);
  private readonly messageService = inject(MessageService);

  private readonly _documents = signal<TicketDocument[]>([]);

  @Input({ required: true }) set documents(docs: TicketDocument[]) {
    this._documents.set(docs);
  }
  @Input({ required: true }) ticketId!: string;
  @Input() currentUserId: string | null = null;
  @Output() attachmentDeleted = new EventEmitter<void>();

  nonImageDocuments = computed(() =>
    this._documents().filter(doc => !this.isImageUrl(this.getFileNameFromUrl(doc.link_file)))
  );

  imageDocuments = computed(() =>
    this._documents().filter(doc => this.isImageUrl(this.getFileNameFromUrl(doc.link_file)))
  );

  // ── Preview state ──────────────────────────────────────────────────────────
  previewVisible = signal(false);
  previewUrl = signal<string | null>(null);
  previewName = signal<string>('');
  previewLoading = signal(false);

  // ── Lightbox zoom / pan / navigation ──────────────────────────────────────
  zoomScale = signal(1);
  panX = signal(0);
  panY = signal(0);
  currentImageIndex = signal(-1);
  dragging = signal(false);
  copyLinkLabel = signal('Copiar link');
  isInternalPreview = signal(false);

  imageTransform = computed(
    () => `translate(${this.panX()}px, ${this.panY()}px) scale(${this.zoomScale()})`
  );
  canGoPrev = computed(() => this.currentImageIndex() > 0);
  canGoNext = computed(() => this.currentImageIndex() < this.imageDocuments().length - 1);

  // ── Preview de arquivos (pdf/md/txt/docx) ──────────────────────────────────
  // Paralelo ao lightbox de imagens acima, que segue intocado.
  filePreviewVisible = signal(false);
  filePreviewSource = signal<FilePreviewSource | null>(null);
  /** Rota da pré-visualização em tela cheia; null esconde o botão de nova aba. */
  filePreviewRouterLink = signal<unknown[] | null>(null);
  private _filePreviewDoc: TicketDocument | null = null;

  // ── Delete state ───────────────────────────────────────────────────────────
  attachmentToDelete = signal<TicketDocument | null>(null);
  showDeleteAttachmentDialog = signal(false);
  deletingAttachment = signal(false);

  // ── Internal tracking ──────────────────────────────────────────────────────
  private _imageLinkForActions: string | null = null;
  private internalImageCache = new Map<string, WritableSignal<string>>();
  private loadingQueue: string[] = [];
  private readonly maxConcurrentLoads = 3;
  private activeLoads = 0;
  private blobUrlsToCleanup = new Set<string>();
  // Blob criado exclusivamente para o preview (não pertence ao cache de thumbnails)
  private _previewBlobUrl: string | null = null;
  private _previewRequestId = 0;

  // ── Drag-to-pan state ──────────────────────────────────────────────────────
  private dragActive = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private panStartX = 0;
  private panStartY = 0;
  private touchId: number | undefined = undefined;

  ngOnDestroy(): void {
    this.blobUrlsToCleanup.forEach(url => URL.revokeObjectURL(url));
    this.blobUrlsToCleanup.clear();
    this.internalImageCache.clear();
  }

  // ── Thumbnail loading ──────────────────────────────────────────────────────

  getImageThumbnailUrl(doc: TicketDocument): WritableSignal<string> {
    if (!doc.link_file) return signal('');

    if (doc.link_file.startsWith('http')) {
      if (this.internalImageCache.has(doc.id)) return this.internalImageCache.get(doc.id)!;
      const s = signal(doc.link_file);
      this.internalImageCache.set(doc.id, s);
      return s;
    }

    if (doc.link_file.startsWith('\\')) {
      if (this.internalImageCache.has(doc.id)) return this.internalImageCache.get(doc.id)!;
      const s = signal(PLACEHOLDER_SVG);
      this.internalImageCache.set(doc.id, s);
      if (!this.loadingQueue.includes(doc.id)) this.loadingQueue.push(doc.id);
      this.processLoadingQueue(doc);
      return s;
    }

    return signal('');
  }

  private processLoadingQueue(doc: TicketDocument): void {
    if (this.activeLoads >= this.maxConcurrentLoads) return;
    const queueIndex = this.loadingQueue.indexOf(doc.id);
    if (queueIndex === -1) return;

    this.activeLoads++;
    this.loadingQueue.splice(queueIndex, 1);
    const imageSignal = this.internalImageCache.get(doc.id)!;

    this.ticketService.getAttachmentBlobTeoremaee(doc as any).subscribe({
      next: blob => {
        const url = URL.createObjectURL(blob);
        this.blobUrlsToCleanup.add(url);
        imageSignal.set(url);
        this.activeLoads--;
        this.processNextInQueue();
      },
      error: () => {
        this.activeLoads--;
        this.processNextInQueue();
      },
    });
  }

  private processNextInQueue(): void {
    if (this.loadingQueue.length > 0) {
      const nextDoc = this.imageDocuments().find(d => d.id === this.loadingQueue[0]);
      if (nextDoc) this.processLoadingQueue(nextDoc);
    }
  }

  // ── Preview / lightbox ─────────────────────────────────────────────────────

  downloadAttachment(doc: TicketDocument): void {
    if (!doc.link_file) {
      this.messageService.add({ severity: 'warn', summary: 'Aviso', detail: 'Caminho do arquivo não disponível' });
      return;
    }

    const fileName = this.getFileNameFromUrl(doc.link_file);
    const isInternal = doc.link_file.startsWith('\\');
    const isImage = this.isImageUrl(fileName);

    if (isImage) {
      const idx = this.imageDocuments().findIndex(d => d.id === doc.id);
      this.currentImageIndex.set(idx);
      this._imageLinkForActions = this._buildImageLink(doc);
      this.isInternalPreview.set(isInternal);
      this.zoomScale.set(1);
      this.panX.set(0);
      this.panY.set(0);
      this._loadImageForPreview(doc, isInternal);
    } else {
      if (isInternal) {
        this.ticketService.downloadAttachmentTeoremaee(doc as any).subscribe({
          next: () => this.messageService.add({ severity: 'success', summary: 'Download Iniciado', detail: 'Baixando anexo da rede interna' }),
          error: () => this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Erro ao fazer download do anexo legado' }),
        });
      } else {
        window.open(doc.link_file, '_blank');
      }
    }
  }

  private _loadImageForPreview(doc: TicketDocument, isInternal: boolean): void {
    this.previewName.set(doc.observation?.trim() || this.getFileNameFromUrl(doc.link_file));

    // Revogar blob exclusivo de preview anterior; blobs do cache de thumbnails não são tocados
    if (this._previewBlobUrl) {
      URL.revokeObjectURL(this._previewBlobUrl);
      this.blobUrlsToCleanup.delete(this._previewBlobUrl);
      this._previewBlobUrl = null;
    }

    if (isInternal) {
      // Reusar blob já carregado pela thumbnail — sem nova request
      const cachedUrl = this._getCachedThumbnailUrl(doc);
      if (cachedUrl) {
        this._previewRequestId++;
        this.previewUrl.set(cachedUrl);
        this.previewLoading.set(false);
        this.previewVisible.set(true);
        return;
      }

      const requestId = ++this._previewRequestId;
      this.previewUrl.set(null);
      this.previewLoading.set(true);
      this.previewVisible.set(true);

      this.ticketService.getAttachmentBlobTeoremaee(doc as any).subscribe({
        next: blob => {
          // Descartar resposta obsoleta se o usuário navegou para outra imagem
          if (requestId !== this._previewRequestId) return;
          const objectUrl = URL.createObjectURL(blob);
          this._previewBlobUrl = objectUrl;
          this.blobUrlsToCleanup.add(objectUrl);
          this.previewUrl.set(objectUrl);
          this.previewLoading.set(false);
        },
        error: () => {
          if (requestId !== this._previewRequestId) return;
          this.previewLoading.set(false);
          this.previewVisible.set(false);
          this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Erro ao carregar pré-visualização da imagem' });
        },
      });
    } else {
      this._previewRequestId++;
      this.previewUrl.set(doc.link_file ?? null);
      this.previewLoading.set(false);
      this.previewVisible.set(true);
    }
  }

  private _getCachedThumbnailUrl(doc: TicketDocument): string | null {
    const cached = this.internalImageCache.get(doc.id);
    if (!cached) return null;
    const url = cached();
    // data: significa que o thumbnail ainda não terminou de carregar
    if (!url || url.startsWith('data:')) return null;
    return url;
  }

  private _buildImageLink(doc: TicketDocument): string | null {
    if (doc.link_file?.startsWith('\\')) {
      return doc.ref ? `https://reverse.teorema.inf.br/teoremaee/api/chamadas/anexos/${doc.ref}` : null;
    }
    return doc.link_file ?? null;
  }

  closePreview(): void {
    if (this._previewBlobUrl) {
      URL.revokeObjectURL(this._previewBlobUrl);
      this.blobUrlsToCleanup.delete(this._previewBlobUrl);
      this._previewBlobUrl = null;
    }
    this._previewRequestId++;
    this.previewVisible.set(false);
    this.previewUrl.set(null);
    this.previewName.set('');
    this.zoomScale.set(1);
    this.panX.set(0);
    this.panY.set(0);
    this.currentImageIndex.set(-1);
    this.dragging.set(false);
    this.dragActive = false;
    this.copyLinkLabel.set('Copiar link');
    this._imageLinkForActions = null;
  }

  onPreviewImageError(): void {
    this.previewUrl.set(null);
    this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível carregar a imagem' });
  }

  // ── Zoom ───────────────────────────────────────────────────────────────────

  zoomIn(): void { this._setZoom(this.zoomScale() * 1.4); }
  zoomOut(): void { this._setZoom(this.zoomScale() / 1.4); }
  zoomReset(): void { this._setZoom(1); }

  private _setZoom(scale: number): void {
    const clamped = Math.min(4, Math.max(0.25, scale));
    this.zoomScale.set(clamped);
    if (clamped <= 1) { this.panX.set(0); this.panY.set(0); }
  }

  // ── Mouse drag-to-pan ──────────────────────────────────────────────────────

  onDragStart(event: MouseEvent): void {
    if (event.button !== 0 || this.zoomScale() <= 1) return;
    event.preventDefault();
    this.dragActive = true;
    this.dragging.set(true);
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.panStartX = this.panX();
    this.panStartY = this.panY();
  }

  @HostListener('document:mousemove', ['$event'])
  onDocMouseMove(event: MouseEvent): void {
    if (!this.dragActive) return;
    this.panX.set(this.panStartX + (event.clientX - this.dragStartX));
    this.panY.set(this.panStartY + (event.clientY - this.dragStartY));
  }

  @HostListener('document:mouseup')
  onDocMouseUp(): void {
    if (!this.dragActive) return;
    this.dragActive = false;
    this.dragging.set(false);
  }

  // ── Scroll-wheel zoom ──────────────────────────────────────────────────────

  onWheelZoom(event: WheelEvent): void {
    event.preventDefault();
    const factor = event.deltaY < 0 ? 1.12 : 0.88;
    const newScale = Math.min(4, Math.max(0.25, this.zoomScale() * factor));
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const mx = event.clientX - rect.left - rect.width / 2;
    const my = event.clientY - rect.top - rect.height / 2;
    const ratio = newScale / this.zoomScale();
    this.panX.set(mx - (mx - this.panX()) * ratio);
    this.panY.set(my - (my - this.panY()) * ratio);
    this.zoomScale.set(newScale);
    if (newScale <= 1) { this.panX.set(0); this.panY.set(0); }
  }

  // ── Touch drag-to-pan ──────────────────────────────────────────────────────

  onTouchStart(event: TouchEvent): void {
    if (event.touches.length !== 1 || this.zoomScale() <= 1) return;
    const t = event.touches[0];
    this.touchId = t.identifier;
    this.dragStartX = t.clientX;
    this.dragStartY = t.clientY;
    this.panStartX = this.panX();
    this.panStartY = this.panY();
    this.dragActive = true;
    this.dragging.set(true);
  }

  onTouchMove(event: TouchEvent): void {
    if (!this.dragActive) return;
    const t = Array.from(event.touches).find(x => x.identifier === this.touchId);
    if (t === undefined) return;
    event.preventDefault();
    this.panX.set(this.panStartX + (t.clientX - this.dragStartX));
    this.panY.set(this.panStartY + (t.clientY - this.dragStartY));
  }

  onTouchEnd(): void {
    this.dragActive = false;
    this.dragging.set(false);
    this.touchId = undefined;
  }

  // ── Navigation ─────────────────────────────────────────────────────────────

  prevImage(): void {
    const idx = this.currentImageIndex();
    if (idx > 0) this._navigateTo(idx - 1);
  }

  nextImage(): void {
    const idx = this.currentImageIndex();
    if (idx < this.imageDocuments().length - 1) this._navigateTo(idx + 1);
  }

  private _navigateTo(idx: number): void {
    const doc = this.imageDocuments()[idx];
    if (!doc) return;
    const isInternal = !!doc.link_file?.startsWith('\\');
    this.currentImageIndex.set(idx);
    this._imageLinkForActions = this._buildImageLink(doc);
    this.isInternalPreview.set(isInternal);
    this.zoomScale.set(1);
    this.panX.set(0);
    this.panY.set(0);
    this._loadImageForPreview(doc, isInternal);
  }

  // ── Toolbar actions ────────────────────────────────────────────────────────

  openCurrentInNewTab(): void {
    const link = this._imageLinkForActions;
    if (link) window.open(link, '_blank');
  }

  copyCurrentLink(): void {
    const link = this._imageLinkForActions;
    if (!link) {
      this.messageService.add({ severity: 'warn', summary: 'Aviso', detail: 'Link não disponível' });
      return;
    }
    navigator.clipboard.writeText(link).then(() => {
      this.copyLinkLabel.set('Copiado!');
      setTimeout(() => this.copyLinkLabel.set('Copiar link'), 2000);
    }).catch(() => {
      this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível copiar o link' });
    });
  }

  copyAttachmentLink(doc: TicketDocument, event?: Event): void {
    event?.stopPropagation();
    let url: string | undefined;

    if (doc.link_file?.startsWith('\\')) {
      if (doc.ref) url = `https://reverse.teorema.inf.br/teoremaee/api/chamadas/anexos/${doc.ref}`;
    } else {
      url = doc.link_file ?? undefined;
    }

    if (!url) {
      this.messageService.add({ severity: 'warn', summary: 'Aviso', detail: 'Link não disponível' });
      return;
    }

    navigator.clipboard.writeText(url).then(() => {
      this.messageService.add({ severity: 'success', summary: 'Copiado!', detail: 'Link do anexo copiado para a área de transferência' });
    }).catch(() => {
      this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível copiar o link' });
    });
  }

  // ── Preview de arquivos ────────────────────────────────────────────────────

  /**
   * Só habilita o botão de visualizar quando há viewer para a extensão e a origem é
   * alcançável. Para todo o resto o chip continua exatamente como era.
   */
  canPreviewAttachment(doc: TicketDocument): boolean {
    return canPreviewTicketAttachment(doc);
  }

  /**
   * Handler NOVO: não passa por `downloadAttachment()`, que segue sendo o clique no chip.
   * Nada é buscado antes daqui — o modal e o viewer só existem depois deste clique.
   */
  openFilePreview(doc: TicketDocument, event?: Event): void {
    event?.stopPropagation();
    this._filePreviewDoc = doc;
    this.filePreviewSource.set(
      buildTicketAttachmentPreviewSource(doc, () =>
        this.ticketService.getAttachmentBlobTeoremaee(doc as any)
      )
    );
    this.filePreviewRouterLink.set(
      this.ticketId ? ['/v3ndor/chamados', this.ticketId, 'anexo', doc.id] : null
    );
    this.filePreviewVisible.set(true);
  }

  /** As ações do modal delegam para os métodos que já existiam, sem duplicar regra. */
  onFilePreviewDownload(): void {
    const doc = this._filePreviewDoc;
    if (doc) this.downloadAttachment(doc);
  }

  onFilePreviewCopyLink(): void {
    const doc = this._filePreviewDoc;
    if (doc) this.copyAttachmentLink(doc);
  }

  // ── Keyboard shortcuts ─────────────────────────────────────────────────────

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.previewVisible() || this.showDeleteAttachmentDialog()) return;
    // Não interceptar atalhos quando um campo de texto está focado
    const target = event.target as HTMLElement;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
    switch (event.key) {
      case 'Escape':
        this.closePreview();
        break;
      case '+':
      case '=':
        this.zoomIn();
        break;
      case '-':
        this.zoomOut();
        break;
      case '0':
        this.zoomReset();
        break;
      case 'ArrowLeft':
        if (this.zoomScale() > 1) { this.panX.update(x => x + 40); }
        else { this.prevImage(); }
        event.preventDefault();
        break;
      case 'ArrowRight':
        if (this.zoomScale() > 1) { this.panX.update(x => x - 40); }
        else { this.nextImage(); }
        event.preventDefault();
        break;
      case 'ArrowUp':
        if (this.zoomScale() > 1) { this.panY.update(y => y + 40); event.preventDefault(); }
        break;
      case 'ArrowDown':
        if (this.zoomScale() > 1) { this.panY.update(y => y - 40); event.preventDefault(); }
        break;
    }
  }

  // ── Attachment delete ──────────────────────────────────────────────────────

  canDeleteAttachment(doc: TicketDocument): boolean {
    return !!this.currentUserId && !!doc.user_id && doc.user_id === this.currentUserId;
  }

  confirmDeleteAttachment(doc: TicketDocument, event: Event): void {
    event.stopPropagation();
    this.attachmentToDelete.set(doc);
    this.showDeleteAttachmentDialog.set(true);
  }

  executeDeleteAttachment(): void {
    const doc = this.attachmentToDelete();
    if (!doc || !this.ticketId) return;
    this.deletingAttachment.set(true);
    this.ticketService.deleteDocument(this.ticketId, doc.id).subscribe({
      next: () => {
        this.deletingAttachment.set(false);
        this.showDeleteAttachmentDialog.set(false);
        this.attachmentToDelete.set(null);
        this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Anexo removido com sucesso' });
        this.attachmentDeleted.emit();
      },
      error: () => {
        this.deletingAttachment.set(false);
        this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Erro ao remover o anexo' });
      },
    });
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  isImageUrl(fileName: string): boolean {
    if (!fileName) return false;
    const lower = fileName.toLowerCase();
    return lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.endsWith('.png') || lower.endsWith('.gif') || lower.endsWith('.webp');
  }

  getFileNameFromUrl(url: string | undefined | null | unknown): string {
    if (typeof url !== 'string' || !url) return 'Anexo';
    try {
      const parts = url.split(/[/\\]/);
      return parts[parts.length - 1] || 'Anexo';
    } catch {
      return 'Anexo';
    }
  }
}
